import { z } from "zod";
import { isValidPostalCode } from "@/lib/validations/postalCode.validation";
import { isValidPhoneNumber } from "libphonenumber-js";
import { compareDesc, parse } from "date-fns";
import { registerFlowBillingEntitySchema } from "@/types/forms/shared/billingEntity.form";
import { CreateUserClientSchema } from "@/types/forms/shared/user.form";
import { i18nMessage } from "@/lib/i18nMessage";

export type { CreateUserClientFormData as UserFormData } from "@/types/forms/shared/user.form";
export { CreateUserClientSchema as userSchema } from "@/types/forms/shared/user.form";
export { DEFAULT_CREATE_USER_CLIENT as DEFAULT_USER } from "@/types/forms/shared/user.form";

const openingSlotSchema = z.object({
  open: z.string().min(1, i18nMessage("shared:forms.register.openTimeRequired")),
  close: z.string().min(1, i18nMessage("shared:forms.register.closeTimeRequired")),
});

export type OpeningSlot = z.infer<typeof openingSlotSchema>;

const openingHoursSchema = z.record(
  z.string(), // "0" to "6" (0=Monday)
  z.array(openingSlotSchema),
);

export type OpeningHours = z.infer<typeof openingHoursSchema>;

export const DEFAULT_OPENING_HOURS: OpeningHours = {
  "0": [{ open: "08:00", close: "18:00" }],
  "1": [{ open: "08:00", close: "18:00" }],
  "2": [{ open: "08:00", close: "18:00" }],
  "3": [{ open: "08:00", close: "18:00" }],
  "4": [{ open: "08:00", close: "18:00" }],
};

export const pickUpLocationSchema = z.object({
  name: z
    .string({
      required_error: i18nMessage("shared:forms.register.pickupLocationNameRequired"),
    })
    .min(1, i18nMessage("shared:forms.register.pickupLocationNameRequired")),
  street: z
    .string({
      required_error: i18nMessage("shared:forms.register.streetRequired"),
    })
    .min(1, i18nMessage("shared:forms.register.streetRequired")),
  postalCode: z
    .string({
      required_error: i18nMessage("shared:forms.register.postalCodeRequired"),
    })
    .min(1, i18nMessage("shared:forms.register.postalCodeRequired"))
    .refine((postalCode) => isValidPostalCode(postalCode), {
      message: i18nMessage("validation:invalidPostalCode"),
    }),
  city: z
    .string({
      required_error: i18nMessage("shared:forms.register.cityRequired"),
    })
    .min(1, i18nMessage("shared:forms.register.cityRequired")),
  contactNumber: z
    .string({
      required_error: i18nMessage("shared:forms.register.phoneRequired"),
    })
    .refine((mobile) => isValidPhoneNumber(mobile), {
      message: i18nMessage("validation:invalidPhoneNumber"),
    }),
  contactNumber2: z
    .string()
    .optional()
    .refine((phone) => !phone || phone === "+33" || isValidPhoneNumber(phone), {
      message: i18nMessage("validation:invalidPhoneNumber"),
    })
    .transform((value) => {
      if (value === "+33") return undefined;
      return value;
    })
    .optional(),

  openingHours: openingHoursSchema.refine(
    (hours) => {
      // Check that at least one day has slots
      const hasAtLeastOneSlot = Object.values(hours).some((slots) => slots.length > 0);
      if (!hasAtLeastOneSlot) return false;

      // Validate each day's slots
      for (const slots of Object.values(hours)) {
        for (let i = 0; i < slots.length; i++) {
          const slot = slots[i];
          const openTime = parse(slot.open, "HH:mm", new Date());
          const closeTime = parse(slot.close, "HH:mm", new Date());

          // open must be before close
          if (compareDesc(openTime, closeTime) !== 1) {
            return false;
          }

          // If there's a next slot, close must be before next open
          if (i < slots.length - 1) {
            const nextOpen = parse(slots[i + 1].open, "HH:mm", new Date());
            if (compareDesc(closeTime, nextOpen) !== 1) {
              return false;
            }
          }
        }
      }
      return true;
    },
    {
      message: i18nMessage("shared:forms.register.openingHoursInconsistent"),
    },
  ),
  isDefault: z.boolean(),
});

export type PickUpLocationFormData = z.infer<typeof pickUpLocationSchema>;

export const RegisterSchema = z.object({
  businessName: z
    .string({
      required_error: i18nMessage("shared:forms.register.businessNameRequired"),
    })
    .min(1, i18nMessage("shared:forms.register.businessNameRequired")),
  billingEntities: z
    .array(registerFlowBillingEntitySchema)
    .min(1, i18nMessage("shared:forms.register.billingEntitiesRequired")),
  admin: CreateUserClientSchema,
  additionalUsers: z.array(CreateUserClientSchema),
  pickUpLocations: z
    .array(pickUpLocationSchema)
    .min(1, i18nMessage("shared:forms.register.pickupLocationsRequired")),
  customerId: z.string(),
});

export type RegisterFormData = z.infer<typeof RegisterSchema>;
